import LeftNavButton, { LeftNavType } from '@omni/kit/components/LeftNavButton';
import { SUBSPLASH_PHONE_AUTH_PROVIDER_ID } from '@omni/kit/constants/identifiers';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import { IModuleCommand } from '@omni/kit/contexts/types';
import { useFeatureFlag } from '@omni/kit/hooks/useFeatureFlag';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { isWithinIframe } from '@omni/kit/utilities/utilities';
import { RouteProp } from '@react-navigation/core/lib/typescript/src/types';
import { useNavigation } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import React, { useEffect, useMemo } from 'react';
import { Platform, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import useShellAuthProviders from '../contexts/ShellContext/hooks/useShellAuthProviders';
import { UNLEASH_TOGGLES } from '../services/UnleashService';
import { dismissReactNativeModal } from '../utilities/NativeHelpers';
import AuthProviderList from './AuthProviderList';
import { AUTH_PROVIDERS_LIST_SCREEN } from './Constants';
import { AuthStackParamsList } from './Types';

const AuthStack = Platform.select({
  default: createStackNavigator(),
  web: createStackNavigator(),
});

interface AuthNavigatorProps {
  appKey: string;
  customReturnUrl?: string;
  dismiss?: () => void;
  headerShown?: boolean;
  renderedFromMessaging?: boolean;
  orgKey?: string;
  route?: RouteProp<AuthStackParamsList, 'auth_stack'>;
  screenProps?: {
    accessToken?: string;
    moduleCommand?: IModuleCommand;
    sapToken?: string;
    tokenUrl?: string;
  };
  targetAuthProviderId: string;
}

function AuthNavigatorContent({
  appKey,
  customReturnUrl,
  dismiss,
  headerShown = true,
  renderedFromMessaging,
  orgKey,
  targetAuthProviderId,
}: AuthNavigatorProps): JSX.Element {
  const { loginEnabledProviders } = useShellAuthProviders();
  const hasPhoneAuthProviderEnabled = loginEnabledProviders.some(
    (provider) => provider.authproviderid === SUBSPLASH_PHONE_AUTH_PROVIDER_ID
  );

  const navigation = useNavigation();
  const { tokens } = useShellContext();

  const userToken = useMemo(() => tokens.user, [tokens.user]);

  /**
   * When a user finishes logging in, the app will receive
   * a new token, and the AuthNavigator should be dismissed.
   */
  useEffect(() => {
    if (hasPhoneAuthProviderEnabled && headerShown && userToken) {
      navigation.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken]);

  const resetNav = () => {
    dismissReactNativeModal();
  };

  const handleOnPress = () => {
    if (hasPhoneAuthProviderEnabled) {
      navigation.goBack();
    } else {
      if (dismiss) {
        dismiss();
      } else {
        resetNav();
      }
    }
  };

  return (
    <SafeAreaProvider>
      <AuthStack.Navigator
        initialRouteName={AUTH_PROVIDERS_LIST_SCREEN}
        screenOptions={{
          headerShown,
          headerStyle: {
            borderBottomWidth: 0,
            elevation: 0,
            shadowColor: 'transparent',
            shadowOpacity: 0,
          },
        }}
      >
        <AuthStack.Screen
          name={AUTH_PROVIDERS_LIST_SCREEN}
          options={() => ({
            headerLeft: () => (
              <View
                style={{
                  marginLeft: Spacing.xl, // alignment with block page dismiss button
                  marginTop: -4, // alignment with block page dismiss button
                }}
              >
                <LeftNavButton
                  iconSize={20}
                  onPress={handleOnPress}
                  title=''
                  type={LeftNavType.Dismiss}
                />
              </View>
            ),
            title: '',
            gestureEnabled: false,
          })}
        >
          {(props) => (
            <KeyboardAwareScrollView
              style={{
                flex: 1,
                backgroundColor: Colors.N0,
              }}
              contentContainerStyle={{
                flexGrow: 1,
                justifyContent: 'center',
              }}
              keyboardShouldPersistTaps='handled'
              showsVerticalScrollIndicator={false}
              enableOnAndroid={!renderedFromMessaging}
              enableAutomaticScroll={true}
              extraScrollHeight={Platform.OS === 'android' ? 20 : 0}
            >
              <AuthProviderList
                {...props}
                appKey={appKey}
                orgKey={orgKey}
                customReturnUrl={customReturnUrl}
                targetAuthProviderId={targetAuthProviderId}
                openInNewTab={isWithinIframe()}
              />
            </KeyboardAwareScrollView>
          )}
        </AuthStack.Screen>
      </AuthStack.Navigator>
    </SafeAreaProvider>
  );
}

function AuthNavigator({
  appKey,
  customReturnUrl,
  dismiss,
  headerShown = true,
  renderedFromMessaging,
  orgKey,
  targetAuthProviderId,
}: AuthNavigatorProps): JSX.Element {
  return (
    <AuthNavigatorContent
      appKey={appKey}
      customReturnUrl={customReturnUrl}
      dismiss={dismiss}
      headerShown={headerShown}
      renderedFromMessaging={renderedFromMessaging}
      orgKey={orgKey}
      targetAuthProviderId={targetAuthProviderId}
    />
  );
}

export default AuthNavigator;
